import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, useDispatch } from "react-redux";
import { Box, Flex, Heading, Link, Spinner, Text } from "theme-ui"
import { dismissHoldingsTransferAsync } from 'features/auth/authSlice';
import { Container } from "~/Common/Container"
import { ProgressCircle } from "~/Common/ProgressCircle";
import { Steps } from "./Steps";
import { setShowVerifySteps } from "features/theme/themeSlice";
import { Button } from "../Button";

export const VerifyStepsInstitutional = connect(
    (state) => ({ user: state.user?.user, settings: state.theme?.settings, showVerifySteps: state.theme?.showVerifySteps, activeProfile: state.user?.activeProfile }),
    { dismissHoldingsTransfer: dismissHoldingsTransferAsync }
)(
    ({ user, settings, showVerifySteps, activeProfile, dismissHoldingsTransfer, ...props }) => {
        const [currentStep, setCurrentStep] = useState(false);
        const [steps, setSteps] = useState(false);
        const { t } = useTranslation();
        const dispatch = useDispatch();
        const individualProfile = activeProfile?.type === 'individual';
        const finaliseEntityLocation = user?.finished_onboarding_details ? !individualProfile ? `/settings/entities/${activeProfile?.uuid}` : '/settings/account' : '/onboarding';

        useEffect(() => {
            const newSteps =
             [
                {
                    label: t('verifyStepsInstitutional.onboardingComplete.label'),
                    heading: t('verifyStepsInstitutional.onboardingComplete.heading'),
                    description: t('verifyStepsInstitutional.onboardingComplete.description'),
                    complete: user.finished_onboarding_details
                },
                {
                    label: t('verifyStepsInstitutional.clientAgreement.label'),
                    heading: t('verifyStepsInstitutional.clientAgreement.heading'),
                    description: user.institution?.institutional_client_agreement_status === 'Pending' ? t('verifyStepsInstitutional.clientAgreement.descriptionNotSent') : t('verifyStepsInstitutional.clientAgreement.descriptionSent'),
                    complete: user.institution?.institutional_client_agreement_status === 'Verified'
                },
                {
                    label: t('verifyStepsInstitutional.standardSettlementInstructions.label'),
                    heading: t('verifyStepsInstitutional.standardSettlementInstructions.heading'),
                    description: user.institution?.institutional_client_agreement_status === 'Pending' ? t('verifyStepsInstitutional.standardSettlementInstructions.description') : t('verifyStepsInstitutional.standardSettlementInstructions.descriptionWaitingVerification'),
                    showGoToSettings: user.institution?.institutional_client_agreement_status === 'Pending',
                    complete: user.institution?.ssi_status === 'Verified'
                },
            ];

            setSteps(newSteps);
            setCurrentStep(newSteps.find(s => !s.complete) ?? false);
        }, [t, user, settings?.broker_to_broker_download, activeProfile, finaliseEntityLocation, individualProfile])

        useEffect(() => {
            // Hide if all steps are complete
            if (!currentStep && Array.isArray(steps)) {
                dispatch(setShowVerifySteps(false));
            }
        }, [currentStep, dispatch, steps]);

        return !showVerifySteps ? null : (
            <Box sx={{
                bg: 'light',
                py: '90px',
            }} {...props}>
                <Container>
                    {Array.isArray(steps) ? (
                        <Flex sx={{
                            flexWrap: 'wrap',
                            justifyContent: 'right',
                            alignItems: 'center',
                            gridGap: '55px',
                        }}>
                            <Box sx={{ maxWidth: ['100%', null, null, '408px'], order: [2, null, null, 0] }}>
                                <ProgressCircle
                                    sx={{ mb: 4 }}
                                    total={steps.length}
                                    progress={steps.indexOf(currentStep)}
                                />
                                <Heading sx={{
                                    fontSize: '28px',
                                    marginBottom: '32px',
                                }}>
                                    {currentStep.heading}
                                </Heading>
                                <Text
                                    as="p"
                                    sx={{
                                        fontSize: '20px',
                                        color: 'dark'
                                    }}
                                >
                                    {currentStep.description}
                                </Text>
                                {currentStep.showGoToSettings && (
                                    <>
                                        <Button as={Link} mr={2} mt={3} href="/settings/institution" sx={{ fontWeight: 'bold' }}>Upload in Account Settings</Button>
                                    </>
                                )}
                            </Box>
                            <Box sx={{ flexBasis: ['100%', null, null, '50%'] }}>
                                <Steps
                                    items={steps}
                                    sx={{
                                        maxWidth: ['100%', null, null, '405px'],
                                    }}
                                />
                            </Box>
                        </Flex>
                    ) : (
                        <Spinner variant="styles.spinner" />
                    )}
                </Container>
            </Box>
        )
    }
);
